import axios from "../axios";
import API_ROULETTE from "../../endpoints/roulette";
import {
    // drawing
    ROULETTE_FETCH_DRAWING_REQUEST,
    ROULETTE_FETCH_DRAWING_SUCCESS,
    ROULETTE_FETCH_DRAWING_FAILURE,

    // recent draw
    ROULETTE_FETCH_RECENT_DRAW_REQUEST,
    ROULETTE_FETCH_RECENT_DRAW_SUCCESS,
    ROULETTE_FETCH_RECENT_DRAW_FAILURE,

    // existing
    ROULETTE_FETCH_RESULT_REPORT,
    ROULETTE_FETCH_WIN_LOSS_REPORT,
    ROULETTE_LIST_BETTING,
    ROULETTE_REQUEST_FETCH_RECENT_BET_REPORT,
    ROULETTE_STATISTIC,

    // OPTIONAL: add these to constant.js if you want full loading+error for all endpoints
    // ROULETTE_FETCH_STATISTIC_REQUEST,
    // ROULETTE_FETCH_STATISTIC_FAILURE,
    // ROULETTE_FETCH_RECENT_BET_REPORT_REQUEST,
    // ROULETTE_FETCH_RECENT_BET_REPORT_FAILURE,
    // ROULETTE_FETCH_RESULT_REPORT_REQUEST,
    // ROULETTE_FETCH_RESULT_REPORT_FAILURE,
    // ROULETTE_FETCH_WIN_LOSS_REPORT_REQUEST,
    // ROULETTE_FETCH_WIN_LOSS_REPORT_FAILURE,
} from "./constant";

import { getRecentSize } from "../user/action";
import _ from "lodash";
import { PAGINATION } from "../rootConstant";
import moment from "moment";
import { hideLoading, showLoading } from "react-redux-loading-bar";

/** ---------------------------------------
 * Helpers
 * ------------------------------------- */

// Normalize axios error -> readable message + status + raw
const parseAxiosError = (error, fallbackMessage = "Request failed") => {
    const status = error?.response?.status ?? null;

    // Prefer API message if present
    const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.msg;

    if (apiMessage) {
        return { message: apiMessage, status, raw: error };
    }

    // Network / CORS / timeout often has no response
    if (error?.request && !error?.response) {
        return { message: "Network error. Please check connection.", status: null, raw: error };
    }

    return { message: error?.message || fallbackMessage, status, raw: error };
};

// Generic async action wrapper to reduce repeated try/catch
const asyncThunk = ({ requestType, successType, failureType, task }) => {
    return async (dispatch) => {
        if (requestType) dispatch({ type: requestType });

        try {
            const result = await task(dispatch);
            if (successType) dispatch({ type: successType, payload: result });
            return result;
        } catch (error) {
            const parsed = parseAxiosError(error);
            if (failureType) dispatch({ type: failureType, payload: parsed.message });
            return Promise.reject(parsed);
        }
    };
};

/** ---------------------------------------
 * API Actions
 * ------------------------------------- */

export const fetchStatisticRoulette = (payload) => {
    const filter = { row: 6, column: 16, skip: 1, ...payload };

    return asyncThunk({
        // Optional if you add constants:
        // requestType: ROULETTE_FETCH_STATISTIC_REQUEST,
        // successType: ROULETTE_STATISTIC,
        // failureType: ROULETTE_FETCH_STATISTIC_FAILURE,
        requestType: null,
        successType: null,   // we dispatch manually to keep existing action type
        failureType: null,

        task: async (dispatch) => {
            const response = await axios.get(API_ROULETTE.FETCH_ROULETTE_STATISTIC, {
                params: filter,
            });

            const data = response?.data?.data ?? { underOverItems: [], numberItems: [] };

            dispatch({
                type: ROULETTE_STATISTIC,
                payload: data,
            });

            return data;
        },
    });
};

export const fetchRouletteDrawingResult = () => {
    return asyncThunk({
        requestType: ROULETTE_FETCH_DRAWING_REQUEST,
        successType: ROULETTE_FETCH_DRAWING_SUCCESS,
        failureType: ROULETTE_FETCH_DRAWING_FAILURE,
        task: async () => {
            const response = await axios.get(API_ROULETTE.FETCH_ROULETTE_DRAWING_RESULT);
            return response?.data?.data ?? null;
        },
    });
};

export const fetchRouletteRecentDraw = () => {
    return asyncThunk({
        requestType: ROULETTE_FETCH_RECENT_DRAW_REQUEST,
        successType: ROULETTE_FETCH_RECENT_DRAW_SUCCESS,
        failureType: ROULETTE_FETCH_RECENT_DRAW_FAILURE,
        task: async () => {
            const response = await axios.get(API_ROULETTE.ROULETTE_FETCH_RECENT_DRAW);
            return response?.data?.data ?? [];
        },
    });
};

export const fetchRouletteRecentBetReport = () => {
    return asyncThunk({
        // Optional if you add constants:
        // requestType: ROULETTE_FETCH_RECENT_BET_REPORT_REQUEST,
        // successType: ROULETTE_REQUEST_FETCH_RECENT_BET_REPORT,
        // failureType: ROULETTE_FETCH_RECENT_BET_REPORT_FAILURE,
        requestType: null,
        successType: null,
        failureType: null,

        task: async (dispatch) => {
            const response = await axios.get(API_ROULETTE.FETCH_ROULETTE_RECENT_BET_REPORT, {
                params: {
                    size: getRecentSize().roulette,
                    page: 1,
                    filterByLotto: "ROULETTE",
                    groupByDraw: "YES",
                },
            });

            const data = response?.data?.data ?? [];

            dispatch({
                type: ROULETTE_REQUEST_FETCH_RECENT_BET_REPORT,
                payload: data,
            });

            return data;
        },
    });
};

export const fetchRouletteResultReport = (payload) => {
    return asyncThunk({
        // Optional if you add constants:
        // requestType: ROULETTE_FETCH_RESULT_REPORT_REQUEST,
        // successType: ROULETTE_FETCH_RESULT_REPORT,
        // failureType: ROULETTE_FETCH_RESULT_REPORT_FAILURE,
        requestType: null,
        successType: null,
        failureType: null,

        task: async (dispatch) => {
            const response = await axios.get(API_ROULETTE.FETCH_ROULETTE_RESULT_REPORT, {
                params: { size: PAGINATION.SIZE, page: 1, ...payload },
            });

            const paging = response?.data?.paging ?? { page: 1, size: PAGINATION.SIZE, totals: 0 };
            const rows = response?.data?.data ?? [];

            const items = rows.map((data) => ({
                ...data,
                code: data.drawCode,
                title: data.drawLabel,
                createdAt: moment(data.createdAt).format("YYYY-MM-DD"),
                createdAtTime: moment(data.createdAt).format("HH:mm"),
            }));

            dispatch({
                type: ROULETTE_FETCH_RESULT_REPORT,
                payload: {
                    items,
                    filter: payload,
                    ...paging,
                },
            });

            return { items, paging };
        },
    });
};

export const fetchRouletteWinLossReport = (payload) => {
    return asyncThunk({
        // Optional if you add constants:
        // requestType: ROULETTE_FETCH_WIN_LOSS_REPORT_REQUEST,
        // successType: ROULETTE_FETCH_WIN_LOSS_REPORT,
        // failureType: ROULETTE_FETCH_WIN_LOSS_REPORT_FAILURE,
        requestType: null,
        successType: null,
        failureType: null,

        task: async (dispatch) => {
            dispatch(showLoading("sectionBar"));

            try {
                const response = await axios.get(API_ROULETTE.FETCH_ROULETTE_WIN_LOSS_REPORT, {
                    params: { filterByLotto: "ROULETTE", ...payload },
                });

                const paging = response?.data?.paging ?? { page: 1, size: PAGINATION.SIZE, totals: 0 };
                const data = response?.data?.data ?? { tickets: [], summary: {} };

                dispatch({
                    type: ROULETTE_FETCH_WIN_LOSS_REPORT,
                    payload: {
                        tickets: data.tickets ?? [],
                        summary: data.summary ?? {},
                        filter: payload,
                        ...paging,
                    },
                });

                return data;
            } finally {
                dispatch(hideLoading("sectionBar"));
            }
        },
    });
};

/** ---------------------------------------
 * Cart / Local Session Actions
 * ------------------------------------- */

export const fetchRouletteListBetting = (payload) => (dispatch) => {
    const betting = JSON.parse(sessionStorage.getItem("betting_roulette") || "[]");

    const item = {
        betList: betting,
        summary: {
            total: betting?.length ? _.sumBy(betting, "amount") : 0,
            currency: "",
            currencySignal: "",
        },
    };

    if (payload?.isClear) {
        dispatch({ type: ROULETTE_LIST_BETTING, payload: item });
        return;
    }

    return item.summary.total;
};

export const addRouletteBetting = (payload) => (dispatch) => {
    let betting = JSON.parse(sessionStorage.getItem("betting_roulette") || "[]");
    if (!Array.isArray(betting)) betting = [];

    betting.push(payload);
    sessionStorage.setItem("betting_roulette", JSON.stringify(betting));

    dispatch(fetchRouletteListBetting({ isClear: true }));
};

export const clearCartRoulette = () => (dispatch) => {
    sessionStorage.removeItem("betting_roulette");
    dispatch(fetchRouletteListBetting({ isClear: true }));
};

export const deleteRouletteBetting = (payload) => (dispatch) => {
    const betting = JSON.parse(sessionStorage.getItem("betting_roulette") || "[]");
    if (!Array.isArray(betting)) return;

    betting.splice(payload.id, 1);
    sessionStorage.setItem("betting_roulette", JSON.stringify(betting));

    dispatch(fetchRouletteListBetting({ isClear: true }));
};

export const rouletteConfirmBettingNow = (callback) => async (dispatch) => {
    const betting = JSON.parse(sessionStorage.getItem("betting_roulette") || "[]");

    const body = {
        drawCode: "",
        gameType: "ROULETTE",
        items: [],
    };

    if (Array.isArray(betting) && betting.length > 0) {
        body.drawCode = betting[betting.length - 1]?.drawCode || "";
        body.items = betting;
    }

    // keep your UI callback
    if (typeof callback === "function") callback();

    try {
        const response = await axios.post(API_ROULETTE.ROULETTE_BETTING_NOW, body);

        sessionStorage.removeItem("betting_roulette");
        dispatch(fetchRouletteListBetting({ isClear: true }));

        return response;
    } catch (error) {
        const parsed = parseAxiosError(error, "Betting failed");
        return Promise.reject(parsed);
    }
};